import React, { lazy } from 'react';
import { Mic } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { WavyDivider } from '../components/WavyDivider';
import { useSeo } from '../lib/useSeo';
export function Story() {
  useSeo({
    title: "Milangela's Story",
    description: 'Read about my journey from a breast cancer diagnosis to discovering the healing power of nature, thoughts, and spirituality.',
    path: '/milangela',
    image: 'https://images.unsplash.com/photo-1499002238440-d264edd596ec?auto=format&fit=crop&q=80&w=2000'
  });
  return <div className="min-h-screen flex flex-col bg-cream" data-id="element-552">
      <Header data-id="element-553" />

      {/* Hero */}
      <section className="relative h-[60vh] min-h-[480px] flex items-center justify-center overflow-hidden" data-id="element-554">
        <div className="absolute inset-0 z-0" data-id="element-555">
          <img src="https://images.unsplash.com/photo-1499002238440-d264edd596ec?auto=format&fit=crop&q=80&w=2000" alt="Lavender field" className="w-full h-full object-cover" data-id="element-556" />
          <div className="absolute inset-0 bg-white/55" data-id="element-557"></div>
        </div>
        <div className="relative z-10 text-center px-6 mt-16" data-id="element-558">
          <h1 className="font-serif text-charcoal mb-2 drop-shadow-md" data-id="element-559">
            <span className="block text-6xl md:text-7xl" data-id="element-560">Milangela</span>
            <span className="block text-3xl md:text-4xl font-script font-bold text-sage-700 mt-2 [text-shadow:0_2px_8px_rgba(255,255,255,0.6)]" data-id="element-561">
              and my story!
            </span>
          </h1>
        </div>
        <div className="absolute bottom-0 left-0 right-0 z-20 pointer-events-none" data-id="element-562">
          <WavyDivider fill="#DCD3DB" data-id="element-563" />
        </div>
      </section>

      {/* Portrait + 3-column story */}
      <section className="bg-[#DCD3DB] py-20 px-6" data-id="element-564">
        <div className="max-w-6xl mx-auto" data-id="element-565">
          <div className="w-64 h-64 md:w-80 md:h-80 mx-auto rounded-full overflow-hidden shadow-soft mb-16" data-id="element-566">
            <img src="https://milangelassoulgarden.com/wp-content/uploads/2022/07/01-MILANGELA-01.png" alt="Portrait of Milangela" loading="lazy" className="w-full h-full object-cover" data-id="element-567" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-10" data-id="element-568">
            <div data-id="element-569">
              <h2 className="font-serif text-2xl text-charcoal mb-5" data-id="element-570">
                I am a soul…
              </h2>
              <p className="text-charcoal-50 font-light leading-relaxed text-sm md:text-justify" data-id="element-571">
                …who sometimes feels she does not belong to this world. Once
                naïve, romantic, and overly sensitive, I used to lead my life as
                if in a trance and stumbled badly at the age of 40. Diagnosed
                with breast cancer I found it impossible to wrap my mind around
                it. Following the hidden programs that were not mine, blind and
                oblivious of what was right, I first felt overwhelmed by what
                laid ahead. I was devastated and thought it was the end, the
                time for me to leave. None of it was logical and it took a while
                before I realized that the traps and pitfalls of my body, in
                reality, were the traps of my mind.
              </p>
            </div>
            <div data-id="element-572">
              <h2 className="font-serif text-2xl text-charcoal mb-5" data-id="element-573">
                The loving support…
              </h2>
              <p className="text-charcoal-50 font-light leading-relaxed text-sm md:text-justify" data-id="element-574">
                …of my family helped tremendously, but still, the rug was pulled
                from under my feet as it dawned on me the battle was mine. No
                one else could possibly know the demons ruling my world, let
                alone, fight them. Love helped me escape quicksand, but only
                after I opened the eyes to the beauty of nature, to the many
                reasons to count my blessings, the healing started. I have since
                been on a quest for a deeper meaning in life. I realized that
                our thoughts are the most powerful tool to completely change
                ourselves and our life.
              </p>
            </div>
            <div data-id="element-575">
              <h2 className="font-serif text-2xl text-charcoal mb-5" data-id="element-576">
                Inspired by…
              </h2>
              <p className="text-charcoal-50 font-light leading-relaxed text-sm md:text-justify" data-id="element-577">
                …spiritual books authored by Shirley MacLaine, Deepak Chopra,
                Eckhart Tolle, James Allen, Wayne Dyer, Michael Singer, Gregg
                Braden, Louise Hay, Bruce Lipton, and many others, I decided to
                go even deeper and joined dr. Joe Dispenza at his Advanced
                Week-Long Retreats, first in Brighton and then in Malta. I
                believe my mission in life is to be a helping hand to those who
                have lost their footing following a disease or challenging
                moments in life.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Podcast */}
      <section className="bg-[#DCD3DB] pb-24 px-6" data-id="element-578">
        <div className="max-w-2xl mx-auto text-center" data-id="element-579">
          <div className="inline-flex w-16 h-16 rounded-full border-2 border-white items-center justify-center mb-6" data-id="element-580">
            <Mic size={28} strokeWidth={1.5} className="text-white" data-id="element-581" />
          </div>
          <p className="font-serif text-2xl text-charcoal mb-2" data-id="element-582">
            Would you like to get to know me?
          </p>
          <p className="text-charcoal-50 mb-8 font-serif text-xl" data-id="element-583">
            Listen to my podcast – Why Milangela's soul garden?
          </p>

          <div className="bg-[#6B5B82] rounded-xl p-5 flex items-center gap-5 max-w-[480px] mx-auto text-left text-white shadow-lg" data-id="element-584">
            <img src="https://milangelassoulgarden.com/wp-content/uploads/2022/07/01-MILANGELA-01.png" alt="Podcast cover" loading="lazy" className="w-28 h-28 rounded-md object-cover" data-id="element-585" />
            <div className="flex-1" data-id="element-586">
              <div className="flex justify-between items-start mb-1" data-id="element-587">
                <h3 className="font-bold text-base leading-tight" data-id="element-588">
                  EP1: Why Milangela's soul garden
                </h3>
                <svg className="w-5 h-5 text-white flex-shrink-0" viewBox="0 0 24 24" fill="currentColor" data-id="element-589">
                  <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.84.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.6.18-1.2.72-1.38 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.56.3z" data-id="element-590" />
                </svg>
              </div>
              <p className="text-[13px] text-white/80 mb-4" data-id="element-591">
                9. jul. • Milangela's soul garden
              </p>
              <div className="flex items-center justify-between" data-id="element-592">
                <div className="flex items-center gap-1.5 text-[12px]" data-id="element-593">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-594">
                    <circle cx="12" cy="12" r="10" strokeWidth="2" data-id="element-595" />
                    <path d="M12 8v8M8 12h8" strokeWidth="2" data-id="element-596" />
                  </svg>
                  Shrani v Spotify
                </div>
                <div className="flex items-center gap-3" data-id="element-597">
                  <span className="text-[11px]" data-id="element-598">10:59</span>
                  <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor" data-id="element-599">
                    <circle cx="12" cy="12" r="12" fill="white" data-id="element-600" />
                    <path d="M10 8l6 4-6 4V8z" fill="#6B5B82" data-id="element-601" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* QUOTE SECTION */}
      <section className="relative bg-[#DCD3DB] py-24 px-6 overflow-hidden" data-id="element-602">
        <div className="max-w-3xl mx-auto text-center relative z-10" data-id="element-603">
          <div className="text-6xl font-serif text-purple-500 leading-none mb-4" data-id="element-604">
            "
          </div>
          <p className="font-serif text-3xl md:text-4xl text-charcoal italic leading-snug mb-5" data-id="element-605">
            Meditation is listening to the divine within.
          </p>
          <p className="text-charcoal-50 text-sm tracking-widest uppercase" data-id="element-606">
            — Edgar Cayce
          </p>
        </div>
      </section>
      <div className="relative bg-[#DCD3DB] pointer-events-none" data-id="element-607">
        <WavyDivider fill="#3A3A38" data-id="element-608" />
      </div>

      <Footer data-id="element-609" />
    </div>;
}